import React, { useEffect, useState } from 'react';
import { dlpService } from '../../services/dlp.service';
import { SteelReportTable } from './SteelReportTable';
import { SteelProgramPivotTable } from './SteelProgramPivotTable';
import { addSteelTableSumRow, mapFileSizeToDescription, SteelTableRow } from '../../lib/dlpSteelTable';

interface Props {
  period: string;
  company: string;
  attachments?: any[];
}

/** 표 이름 → Raw Data 링크 attachment의 linkId (IPT-441 후속, TRD §3.2) */
const RAW_DATA_LINK_ID: Record<string, string> = {
  '사외메신저 서비스별 업로드 현황': 'steel_messenger_upload_raw',
  '생성형 AI 서비스별 업로드 현황': 'steel_genai_upload_raw',
};

/**
 * 2026-09-15 후속 확인 — 허용/차단 프로그램별 부서 TOP5는 flat 세로 테이블이 아니라
 * 프로그램별 피벗 표(SteelProgramPivotTable)로 렌더링해야 한다.
 */
const PROGRAM_PIVOT_TABLE_LABELS: Record<string, string> = {
  '허용 프로그램별 부서 TOP5': '첨부 건수',
  '차단 프로그램별 부서 TOP5': '차단 건수',
};

/**
 * 합계 행이 필요한 표(SPEC F1) — 표 이름 → { 합계 대상 컬럼, 라벨 컬럼 }
 * IPT-622(2026-09-16) — 파일크기 두 표의 키를 실제 Privacy-i API 원본 이름으로 수정했다.
 * ('메신저 파일 용량대별 탐지 현황'/'생성형AI 파일 용량대별 탐지 현황'로 하드코딩돼 있었는데
 * 실제 API 키는 '메신저 파일크기 현황'/'생성형AI 파일크기 현황' — cron-worker VM 실측 확인.
 * 이름이 달라 매칭이 안 돼 IPT-441 Q4의 합계 행·예상 파일 종류 보강이 이 두 표에는
 * 처음부터 한 번도 적용된 적이 없었다.)
 */
const SUM_TABLE_CONFIG: Record<string, { sumColumns: string[]; labelColumn: string }> = {
  '사외메신저 서비스별 업로드 현황': { sumColumns: ['이용 건수'], labelColumn: '서비스' },
  '생성형 AI 서비스별 업로드 현황': { sumColumns: ['이용 건수'], labelColumn: '서비스' },
  '메신저 파일크기 현황': { sumColumns: ['건수'], labelColumn: '파일크기' },
  '생성형AI 파일크기 현황': { sumColumns: ['건수'], labelColumn: '파일크기' },
};

/** SPEC F2 — 용량대별 표에는 "예상 파일 종류" 설명 컬럼을 고정 매핑표로 추가한다 (실제 API 원본 이름 기준) */
const FILE_SIZE_TABLE_NAMES = new Set(['메신저 파일크기 현황', '생성형AI 파일크기 현황']);

/** IPT-622 — API 원본 표 이름 → 화면 표시 제목 (원본은 요구사항에서 정한 명칭과 달랐다) */
const DISPLAY_TITLE_MAP: Record<string, string> = {
  '메신저 파일크기 현황': '사외 메신저 업로드 파일 용량대별 탐지 현황',
  '생성형AI 파일크기 현황': '생성형 AI 업로드 파일 용량대별 탐지 현황',
};

/**
 * IPT-622/623 — 표별 컬럼 순서·라벨 강제 (운영 캐시 JSONB 왕복 시 원본 API 키
 * 순서가 보존되지 않아, 명시적으로 지정해야 하는 표만 등록한다).
 */
const COLUMN_ORDER: Record<string, string[]> = {
  '파일 첨부 건수 현황': ['구분', '건수'],
  '사외메신저 서비스별 업로드 현황': ['서비스', '이용 건수', '비중'],
  '생성형 AI 서비스별 업로드 현황': ['서비스', '이용 건수', '비중'],
  '메신저 파일크기 현황': ['파일크기', '예상 파일 종류', '건수', '비율'],
  '생성형AI 파일크기 현황': ['파일크기', '예상 파일 종류', '건수', '비율'],
};

const COLUMN_LABELS: Record<string, Record<string, string>> = {
  '메신저 파일크기 현황': { 파일크기: '업로드 파일 용량 구분', 건수: '탐지 건수', 비율: '비중' },
  '생성형AI 파일크기 현황': { 파일크기: '업로드 파일 용량 구분', 건수: '탐지 건수', 비율: '비중' },
};

function applyTableRules(tableName: string, rows: Record<string, string>[]): SteelTableRow[] {
  let processed: Record<string, string>[] = rows;
  if (FILE_SIZE_TABLE_NAMES.has(tableName)) {
    processed = processed.map((row) => ({ ...row, '예상 파일 종류': mapFileSizeToDescription(row['파일크기']) }));
  }
  const sumConfig = SUM_TABLE_CONFIG[tableName];
  if (sumConfig) {
    return addSteelTableSumRow(processed, sumConfig);
  }
  return processed;
}

/** 세아제강 계열 DLP — 파일 첨부 탭 (IPT-441, Privacy-i API report_type=upload) */
export const SteelFileUploadTab: React.FC<Props> = ({ period, company, attachments = [] }) => {
  const [tables, setTables] = useState<Record<string, Record<string, string>[]>>({});
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!period || !company) return;
    setIsLoading(true);
    dlpService
      .getSteelReport(period, company)
      .then((res) => setTables(res.reports.upload))
      .catch(() => setTables({}))
      .finally(() => setIsLoading(false));
  }, [period, company]);

  if (isLoading) return <div>로딩 중...</div>;

  return (
    <div className="tab-panel steel-file-upload-tab">
      <h2>파일 첨부</h2>
      {Object.entries(tables).map(([tableName, rows]) => {
        const pivotLabel = PROGRAM_PIVOT_TABLE_LABELS[tableName];
        if (pivotLabel) {
          return <SteelProgramPivotTable key={tableName} title={tableName} totalCountLabel={pivotLabel} rows={rows} />;
        }
        const linkId = RAW_DATA_LINK_ID[tableName];
        const linkUrl = linkId ? attachments.find((a) => a.linkId === linkId)?.linkUrl : undefined;
        return (
          <SteelReportTable
            key={tableName}
            title={DISPLAY_TITLE_MAP[tableName] ?? tableName}
            rows={applyTableRules(tableName, rows)}
            linkUrl={linkUrl}
            columnOrder={COLUMN_ORDER[tableName]}
            columnLabels={COLUMN_LABELS[tableName]}
          />
        );
      })}
      {Object.keys(tables).length === 0 && <div>데이터가 없습니다.</div>}
    </div>
  );
};
